import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Carousel, Button, Modal, Alert } from 'react-bootstrap';
import { useInView } from 'react-intersection-observer';
import IMAGES from '../assets/index.js'; // Add .js extension
import ReviewForm from './ReviewForm.js'; // Add .js extension
import { submitReview, fetchReviews } from '../services/reviewService.js'; // Add .js extension
import { trackEvent } from './Analytics.js'; // Import the trackEvent function

// Move defaultReviews outside the component to avoid dependency issues
const defaultReviews = [
  {
    id: '1',
    name: "Pooja Sharma",
    role: "Regular Member",
    content: "I've been practicing yoga for years, but these classes have taken my practice to a whole new level. The instructors are incredibly knowledgeable and supportive.",
    rating: 5,
    date: "2023-05-15"
  },
  {
    id: '2',
    name: "Nilam Patel",
    role: "Beginner",
    content: "As someone completely new to yoga, I was nervous to start. But the welcoming environment and patient guidance have made this an incredible journey of discovery.",
    rating: 5,
    date: "2023-06-22"
  },
  {
    id: '3',
    name: "Meena Desai",
    role: "Advanced Practitioner",
    content: "The advanced classes offer the perfect balance of challenge and support. I've grown tremendously in both strength and mindfulness.",
    rating: 4,
    date: "2023-07-10"
  }
];

function Testimonials() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingReviews, setFetchingReviews] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const carouselRef = useRef(null);
  const [autoPlay, setAutoPlay] = useState(true);
  const intervalRef = useRef(null);
  const [error, setError] = useState(null);

  // Animation trigger when section comes into view
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });
  
  // Load reviews from MongoDB Atlas via API endpoint
  useEffect(() => {
    const getReviews = async () => {
      setFetchingReviews(true);
      setError(null);
      
      try {
        const fetchedReviews = await fetchReviews();
        console.log('Using fetched reviews data');
        
        if (fetchedReviews && fetchedReviews.length > 0) {
          setReviews(fetchedReviews);
        } else {
          console.log('No reviews returned, using default reviews');
          setReviews(defaultReviews);
        }
      } catch (err) {
        console.error('Error fetching reviews:', err);
        setError('Failed to load reviews. Showing sample reviews instead.');
        setReviews(defaultReviews);
      } finally {
        setFetchingReviews(false);
      }
    };
    
    getReviews();
  }, []);
  
  // Modal handling functions
  const handleOpenModal = () => {
    setAutoPlay(false); // Pause carousel when opening modal
    setShowModal(true);
  };
  
  const handleCloseModal = () => {
    setShowModal(false);
    setAutoPlay(true); // Resume carousel when closing modal
  };
  
  // Handle submission of new review
  const handleSubmitReview = async (reviewData) => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('Submitting new review:', reviewData);
      const newReview = await submitReview(reviewData);
      
      // Track successful review submission
      trackEvent('review_submit', {
        'rating': reviewData.rating
      });
      
      // Update the local state with the new review
      setReviews(prevReviews => [newReview, ...prevReviews]);
      setError(null);
      
      // Show success message or toast notification
      console.log('Review submitted successfully');
    } catch (err) {
      console.error("Error submitting review:", err);
      
      // Track error in review submission
      trackEvent('review_submit_error', {
        'error_type': err.message
      });
      
      setError("Failed to submit your review. Please try again later.");
      
      // Add more detailed error logging
      if (err.response) {
        console.error('Response error:', err.response);
      }
    } finally {
      setLoading(false);
      handleCloseModal();
    }
  };
  
  // Set up auto-scrolling for the carousel
  useEffect(() => {
    if (autoPlay && reviews.length > 0) {
      intervalRef.current = setInterval(() => {
        if (carouselRef.current) {
          carouselRef.current.next();
        }
      }, 5000); // Change slide every 5 seconds
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [autoPlay, reviews.length]);
  
  // Generate star rating display
  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <i 
          key={i} 
          className={`fa ${i <= rating ? 'fa-star' : 'fa-star-o'}`} 
          style={{ color: '#f4a111', marginRight: '3px' }}
        ></i>
      );
    }
    return stars;
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  // Generate initials for avatar
  const getInitials = (name) => {
    if (!name) return "??";
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <div id="testimonials" ref={ref} className="pepole">
      <Container>
        <Row className="justify-content-center">
          <Col md={12}>
            <div className={`titlepage ${inView ? 'animate__animated animate__fadeInDown' : ''}`}>
              <h2>What People Say</h2>
              <span>Read testimonials from our community</span>
            </div>
          </Col>
        </Row>
        
        {error && (
          <Alert 
            variant="warning" 
            className="text-center mb-4"
            style={{
              backgroundColor: 'rgba(244, 161, 17, 0.1)',
              borderColor: 'rgba(244, 161, 17, 0.3)',
              color: '#f4a111'
            }}
          >
            {error}
            <div className="mt-2">
              <Button 
                variant="outline-warning" 
                size="sm" 
                onClick={() => window.location.reload()}
                style={{
                  borderColor: '#f4a111',
                  color: '#f4a111'
                }}
              >
                Try Again
              </Button>
            </div>
          </Alert>
        )}
        
        {fetchingReviews ? (
          <div className="text-center py-5">
            <div 
              className="spinner-border" 
              role="status"
              style={{ color: '#f4a111' }}
            >
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading reviews...</p>
          </div>
        ) : reviews.length > 0 ? (
          <div className={`testimonial-wrapper ${inView ? 'animate__animated animate__fadeIn' : ''}`}>
            <Carousel 
              ref={carouselRef}
              interval={null} // We're manually controlling interval
              indicators={false} // Set to false to hide the indicators
              controls={true}
              className="testimonial-carousel fixed-height-carousel"
            >
              {reviews.map((review) => (
                <Carousel.Item key={review.id || review._id}>
                  <div className="testimonial-card">
                    <div className="testimonial-quote">
                      <img src={IMAGES.tQuote} alt="quote" className="quote-icon" />
                    </div>
                    
                    <div className="testimonial-content-wrapper">
                      <p className="testimonial-content">{review.content}</p>
                    </div>
                    
                    <div className="testimonial-footer">
                      <div className="testimonial-rating">
                        {renderStars(review.rating)}
                      </div>
                      
                      <div className="testimonial-author">
                        <div className="author-avatar">
                          {getInitials(review.name)}
                        </div>
                        <div className="author-details">
                          <h4>{review.name}</h4>
                          <p>{review.role} <span className="review-date">• {formatDate(review.date)}</span></p>
                        </div>
                      </div>
                    </div>
                  </div>
                </Carousel.Item>
              ))}
            </Carousel>
          </div>
        ) : (
          <div className="text-center py-5">
            <p>No reviews yet. Be the first to share your experience!</p>
          </div>
        )}
        
        <Row className="justify-content-center mt-3">
          <Col md={4} className="text-center">
            <Button 
              onClick={handleOpenModal}
              className="yogaday_btn_horizontal"
              variant="warning"
              size="lg"
            >
              Share Your Experience
            </Button>
          </Col>
        </Row>
      </Container>
      
      {/* Modal for submitting a new review */}
      <Modal 
        show={showModal} 
        onHide={handleCloseModal} 
        centered 
        className="review-modal"
        backdrop="static"
        size="lg" // Make modal larger to fit dropdown better
      >
        <Modal.Header 
          closeButton
          style={{
            backgroundColor: '#f4a111',
            color: 'white',
            borderBottom: 'none',
            position: 'relative',
            paddingRight: '50px' // Add extra padding for the close button
          }}
        >
          <Modal.Title style={{ fontWeight: 'bold' }}>Share Your Experience</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: 'rgba(0, 0, 0, 0.85)', color: '#fff', padding: '25px' }}>
          <ReviewForm onSubmit={handleSubmitReview} isLoading={loading} />
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default Testimonials;
